/* Variant A — "Editorial Pro"
 * Refined, Bloomberg-adjacent finance product.
 * Light neutral + single accent (deep teal), serif display numbers,
 * dense but breathable, asymmetric grid.
 */

const VA = {
  bg:         '#F6F4EF',   // warm off-white paper
  surface:    '#FFFFFF',
  ink:        '#0E1411',   // near-black, warm
  inkSoft:    '#4A5048',
  inkFaint:   '#8B8F89',
  rule:       '#E5E2DA',
  ruleSoft:   '#EEEBE3',
  accent:     '#0B5F5A',   // deep teal
  accentSoft: '#E6EEED',
  up:         '#0B5F5A',
  down:       '#9B3330',
  warn:       '#A56A1F',
  sansDisplay:'"Söhne","Inter","Helvetica Neue",system-ui,sans-serif',
  serif:      '"Source Serif 4","GT Sectra","Fraunces",Georgia,serif',
  mono:       '"JetBrains Mono","SF Mono",Menlo,monospace',
};

function VariantA() {
  const totalPnl = TRADERS.reduce((s, t) => s + t.pnl, 0);
  const totalLots = TRADERS.reduce((s, t) => s + t.lots, 0);
  const totalTrades = TRADERS.reduce((s, t) => s + t.trades, 0);
  const avgWin = TRADERS.reduce((s, t) => s + t.win, 0) / TRADERS.length;

  const sparkMain = makeSparkSeries(17, 32, 0, 3.2, 24);
  sparkMain.push(sparkMain[sparkMain.length - 1] + 30);

  return (
    <div style={{
      width: 1680, minHeight: 1100,
      background: VA.bg,
      color: VA.ink,
      fontFamily: VA.sansDisplay,
      fontFeatureSettings: '"ss01","cv11","tnum"',
      display: 'grid',
      gridTemplateColumns: '244px 1fr',
    }}>
      {/* ═══════ Sidebar ═══════ */}
      <aside style={{
        background: VA.bg,
        borderRight: `1px solid ${VA.rule}`,
        padding: '28px 0 20px',
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, alignSelf: 'start',
      }}>
        {/* Brand */}
        <div style={{ padding: '0 24px 28px', borderBottom: `1px solid ${VA.rule}`, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 6,
              background: VA.accent, color: '#fff',
              display: 'grid', placeItems: 'center',
              fontFamily: VA.serif, fontWeight: 600, fontSize: FontSizes.base, letterSpacing: '-0.02em',
            }}>G</div>
            <div>
              <div style={{ fontFamily: VA.serif, fontSize: FontSizes.base, fontWeight: 500, letterSpacing: '-0.01em', lineHeight: 1.1 }}>
                Genesis
              </div>
              <div style={{ fontSize: FontSizes.trading_md, color: VA.inkFaint, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>
                International Capital
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '0 12px' }}>
          {NAV_GROUPS.map((g, gi) => (
            <div key={gi} style={{ marginBottom: 22 }}>
              <div style={{
                fontSize: FontSizes.trading_sm, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: VA.inkFaint, padding: '0 12px 8px',
              }}>
                {g.label}
              </div>
              {g.items.map((it, i) => {
                const active = it.active;
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px',
                    borderRadius: 5,
                    fontSize: FontSizes.trading_md,
                    color: active ? VA.ink : VA.inkSoft,
                    background: active ? '#fff' : 'transparent',
                    fontWeight: active ? 500 : 400,
                    position: 'relative',
                    boxShadow: active ? '0 1px 0 rgba(14,20,17,0.04)' : 'none',
                    border: active ? `1px solid ${VA.rule}` : '1px solid transparent',
                    marginBottom: 1,
                  }}>
                    {active && (
                      <div style={{
                        position: 'absolute', left: -12, top: 10, bottom: 10,
                        width: 2, background: VA.accent, borderRadius: 2,
                      }} />
                    )}
                    <Icon name={it.icon} size={15} />
                    <span style={{ flex: 1 }}>{it.name}</span>
                    {it.ext && <Icon name="ext" size={11} stroke={2} className="ext" />}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User */}
        <div style={{ padding: '14px 20px 0', borderTop: `1px solid ${VA.rule}`, marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: '#D6CEB9', color: VA.ink,
              display: 'grid', placeItems: 'center',
              fontSize: FontSizes.xxs, fontWeight: 600,
            }}>MA</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: FontSizes.trading_md, fontWeight: 500 }}>Mason Chen</div>
              <div style={{ fontSize: FontSizes.trading_md, color: VA.inkFaint }}>gsinvest009 · Trader</div>
            </div>
            <Icon name="log-out" size={13} className="" />
          </div>
        </div>
      </aside>

      {/* ═══════ Main ═══════ */}
      <main style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Top ticker + quick links */}
        <header style={{
          display: 'flex', alignItems: 'center',
          padding: '14px 40px', gap: 24,
          borderBottom: `1px solid ${VA.rule}`,
          background: VA.bg,
          fontSize: FontSizes.xxs,
        }}>
          {/* Market ticker */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flex: 1, fontFamily: VA.mono, fontSize: FontSizes.trading_xl }}>
            {[
              ['加權', '22,184.05', '+0.42%', true],
              ['櫃買', '  228.91', '+0.18%', true],
              ['台指近', '22,185.0', '+0.38%', true],
              ['小台近', '22,210.0', '+0.41%', true],
              ['美元', '31.82', '−0.12%', false],
            ].map(([k, v, d, up], i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                <span style={{ color: VA.inkFaint, fontSize: FontSizes.trading_md, letterSpacing: '0.05em' }}>{k}</span>
                <span style={{ color: VA.ink, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{v}</span>
                <span style={{ color: up ? VA.up : VA.down, fontSize: FontSizes.trading_md }}>{d}</span>
              </div>
            ))}
          </div>
          {/* Quick links */}
          <div style={{ display: 'flex', gap: 2, color: VA.inkSoft }}>
            {QUICK_LINKS.map((q, i) => (
              <div key={i} style={{ padding: '4px 10px', fontSize: FontSizes.xxs, cursor: 'pointer' }}>{q}</div>
            ))}
          </div>
          <div style={{ width: 1, height: 16, background: VA.rule }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Icon name="bell" size={15} className="" />
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 10px', borderRadius: 4,
              background: VA.accent, color: '#fff', fontSize: FontSizes.trading_xl, fontWeight: 500,
            }}>
              <Icon name="message" size={13} stroke={2} />
              戰情室 · 3
            </div>
          </div>
        </header>

        {/* Page body */}
        <div style={{ padding: '36px 40px 40px', flex: 1 }}>
          {/* Masthead */}
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            paddingBottom: 22, borderBottom: `2px solid ${VA.ink}`, marginBottom: 28,
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
                <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.24em', textTransform: 'uppercase', color: VA.inkFaint }}>
                  交易儀表板 · Trading Desk
                </div>
                <div style={{ width: 24, height: 1, background: VA.inkFaint }} />
                <div style={{ fontSize: FontSizes.trading_lg, color: VA.inkFaint, fontFamily: VA.mono }}>
                  {new Date().toLocaleDateString('zh-TW', { year:'numeric', month:'long', day:'numeric', weekday:'long' })}
                </div>
              </div>
              <h1 style={{
                fontFamily: VA.serif, fontSize: FontSizes.xl, fontWeight: 400,
                letterSpacing: '-0.02em', lineHeight: 1,
                margin: 0, color: VA.ink,
              }}>
                Morning, Mason. <span style={{ color: VA.inkFaint, fontStyle: 'italic' }}>今日觀點。</span>
              </h1>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{
                border: `1px solid ${VA.rule}`, background: '#fff',
                padding: '9px 14px', borderRadius: 4, fontSize: FontSizes.trading_md, color: VA.ink,
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
              }}>
                <Icon name="refresh" size={13} /> 重新整理
              </button>
              <button style={{
                border: `1px solid ${VA.rule}`, background: '#fff',
                padding: '9px 14px', borderRadius: 4, fontSize: FontSizes.trading_md, color: VA.ink,
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
              }}>
                <Icon name="download" size={13} /> 匯出
              </button>
              <button style={{
                border: 'none', background: VA.ink, color: '#fff',
                padding: '9px 16px', borderRadius: 4, fontSize: FontSizes.trading_md, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
              }}>
                <Icon name="plus" size={14} stroke={2.3} /> 新增交易
              </button>
            </div>
          </div>

          {/* Lede numbers row — editorial pull-quote scale */}
          <div style={{
            display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1fr',
            borderBottom: `1px solid ${VA.rule}`, paddingBottom: 28, marginBottom: 28,
          }}>
            {/* Main headline figure */}
            <div style={{ paddingRight: 40, borderRight: `1px solid ${VA.rule}` }}>
              <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: VA.inkFaint, marginBottom: 14 }}>
                今日累計損益 · Realized + Open P&L
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18 }}>
                <div style={{
                  fontFamily: VA.serif, fontSize: FontSizes.xxl, fontWeight: 400,
                  letterSpacing: '-0.035em', lineHeight: 0.92,
                  color: VA.up, fontVariantNumeric: 'tabular-nums',
                }}>
                  +${(totalPnl/1000).toFixed(1)}<span style={{ fontSize: FontSizes.xl, color: VA.inkFaint }}>K</span>
                </div>
                <div style={{ paddingBottom: 8 }}>
                  <div style={{
                    display:'inline-flex', alignItems:'center', gap: 4,
                    padding: '3px 8px', borderRadius: 3,
                    background: VA.accentSoft, color: VA.accent,
                    fontSize: FontSizes.trading_lg, fontWeight: 500,
                  }}>
                    <Icon name="trending-up" size={11} stroke={2.2} /> +12.4% WoW
                  </div>
                  <div style={{ fontSize: FontSizes.trading_lg, color: VA.inkFaint, marginTop: 6, fontFamily: VA.mono }}>
                    vs. 上週 $487,200
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
                {['7D','30D','ALL'].map((p, i) => (
                  <div key={i} style={{
                    padding: '4px 10px', fontSize: FontSizes.trading_lg,
                    border: `1px solid ${i===2 ? VA.ink : VA.rule}`,
                    background: i===2 ? VA.ink : 'transparent',
                    color: i===2 ? '#fff' : VA.inkSoft,
                    borderRadius: 3, fontFamily: VA.mono, letterSpacing: '0.05em',
                  }}>{p}</div>
                ))}
              </div>
            </div>

            {[
              { k: '總筆數', v: totalTrades, sub: '8 留倉 · 10 已平', tint: VA.ink },
              { k: '總口數', v: totalLots, sub: '多 3.2× 空', tint: VA.ink },
              { k: '平均勝率', v: fmt.pct(avgWin), sub: '6 位交易員', tint: VA.up },
            ].map((x, i) => (
              <div key={i} style={{ padding: '0 28px', borderRight: i<2 ? `1px solid ${VA.rule}`:'none' }}>
                <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.16em', textTransform: 'uppercase', color: VA.inkFaint, marginBottom: 14 }}>
                  {x.k}
                </div>
                <div style={{
                  fontFamily: VA.serif, fontSize: FontSizes.xl, fontWeight: 400,
                  letterSpacing: '-0.03em', lineHeight: 1,
                  color: x.tint, fontVariantNumeric: 'tabular-nums',
                }}>
                  {x.v}
                </div>
                <div style={{ fontSize: FontSizes.xxs, color: VA.inkFaint, marginTop: 10 }}>{x.sub}</div>
              </div>
            ))}
          </div>

          {/* Main chart + trader leaderboard */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 28, marginBottom: 32 }}>
            {/* Chart */}
            <div style={{
              background: VA.surface, border: `1px solid ${VA.rule}`, borderRadius: 6,
              padding: '22px 24px 18px',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA.inkFaint, marginBottom: 6 }}>
                    Figure 01
                  </div>
                  <h3 style={{ fontFamily: VA.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }}>
                    交易員累計損益
                  </h3>
                  <div style={{ fontSize: FontSizes.xxs, color: VA.inkFaint, marginTop: 3 }}>過去 30 個交易日 · 基礎幣 TWD</div>
                </div>
                <div style={{ display: 'flex', gap: 4, background: '#F5F2EB', padding: 3, borderRadius: 4, fontSize: FontSizes.trading_lg }}>
                  {['損益','口數','筆數','勝率'].map((m,i)=>(
                    <div key={i} style={{
                      padding: '4px 10px', borderRadius: 3,
                      background: i===0 ? '#fff' : 'transparent',
                      color: i===0 ? VA.ink : VA.inkSoft,
                      fontWeight: i===0 ? 500 : 400,
                      boxShadow: i===0 ? '0 1px 0 rgba(0,0,0,0.04)' : 'none',
                    }}>{m}</div>
                  ))}
                </div>
              </div>
              {/* Mini chart */}
              <EditorialChart />
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                paddingTop: 14, marginTop: 14, borderTop: `1px solid ${VA.ruleSoft}`,
                fontSize: FontSizes.trading_lg, color: VA.inkFaint, fontFamily: VA.mono,
              }}>
                <span>09:00</span><span>10:30</span><span>12:00</span><span>13:30</span><span>15:00</span>
              </div>
            </div>

            {/* Leaderboard */}
            <div style={{
              background: VA.surface, border: `1px solid ${VA.rule}`, borderRadius: 6,
              padding: '22px 24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA.inkFaint, marginBottom: 6 }}>
                    Figure 02
                  </div>
                  <h3 style={{ fontFamily: VA.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }}>
                    交易員排行
                  </h3>
                </div>
                <div style={{ fontSize: FontSizes.trading_lg, color: VA.inkFaint, display: 'flex', alignItems: 'center', gap: 4 }}>
                  本週 <Icon name="chevron-down" size={12} />
                </div>
              </div>

              {TRADERS.map((t, i) => {
                const max = Math.max(...TRADERS.map(x => Math.abs(x.pnl)));
                const widthPct = Math.abs(t.pnl) / max * 100;
                return (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '18px 28px 1fr auto',
                    gap: 12, alignItems: 'center',
                    padding: '10px 0', borderBottom: i < TRADERS.length - 1 ? `1px solid ${VA.ruleSoft}` : 'none',
                  }}>
                    <div style={{ fontFamily: VA.mono, fontSize: FontSizes.trading_md, color: VA.inkFaint }}>
                      0{i+1}
                    </div>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%',
                      background: ['#D6CEB9','#C3D6CC','#E4D5C6','#D4C8DB','#DBCEC8','#C8D0DB'][i],
                      color: VA.ink,
                      display: 'grid', placeItems: 'center',
                      fontSize: FontSizes.trading_sm, fontWeight: 600,
                    }}>{t.name.slice(0,1).toUpperCase()}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: FontSizes.xs, fontWeight: 500 }}>{t.name}</span>
                        <span style={{
                          fontFamily: VA.mono, fontSize: FontSizes.xxs,
                          color: t.pnl >= 0 ? VA.up : VA.down,
                          fontVariantNumeric: 'tabular-nums',
                        }}>{fmt.pnl(t.pnl)}</span>
                      </div>
                      <div style={{ height: 3, background: VA.ruleSoft, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                        <div style={{
                          position: 'absolute', top: 0, bottom: 0, left: 0,
                          width: `${widthPct}%`,
                          background: t.pnl >= 0 ? VA.accent : VA.down,
                          opacity: 0.85,
                        }} />
                      </div>
                    </div>
                    <div style={{ fontSize: FontSizes.trading_lg, color: VA.inkFaint, fontFamily: VA.mono, textAlign: 'right' }}>
                      {t.trades}筆 · {fmt.pct(t.win)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Two-up sub-charts */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginBottom: 32 }}>
            <SubCard title="持倉分佈 · By Symbol" fig="03">
              <DistBars />
            </SubCard>
            <SubCard title="每日交易量 · Daily Flow" fig="04">
              <DailyBars />
            </SubCard>
          </div>

          {/* Positions table */}
          <div style={{ background: VA.surface, border: `1px solid ${VA.rule}`, borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '20px 24px', borderBottom: `1px solid ${VA.rule}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
                <div>
                  <div style={{ fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA.inkFaint, marginBottom: 4 }}>
                    Ledger
                  </div>
                  <h3 style={{ fontFamily: VA.serif, fontSize: FontSizes.lg, fontWeight: 400, margin: 0, letterSpacing: '-0.015em' }}>
                    交易紀錄 <span style={{ color: VA.inkFaint, fontStyle: 'italic' }}>— 本日</span>
                  </h3>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, fontSize: FontSizes.xxs }}>
                {[
                  ['全部', POSITIONS.length, true],
                  ['留倉', POSITIONS.filter(p=>p.st==='holding').length],
                  ['已平', POSITIONS.filter(p=>p.st==='closed').length],
                ].map(([l, n, a], i) => (
                  <div key={i} style={{
                    padding: '6px 12px', borderRadius: 4,
                    background: a ? VA.ink : 'transparent',
                    color: a ? '#fff' : VA.inkSoft,
                    border: a ? 'none' : `1px solid ${VA.rule}`,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    {l}
                    <span style={{
                      fontSize: FontSizes.trading_sm, padding: '1px 6px', borderRadius: 8,
                      background: a ? 'rgba(255,255,255,0.2)' : VA.ruleSoft,
                    }}>{n}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
              <thead>
                <tr style={{ background: '#FAF8F3', fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: VA.inkFaint }}>
                  <th style={{ padding: '10px 16px 10px 24px', textAlign: 'left', fontWeight: 500 }}>負責人</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>標的</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500 }}>方向</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>口數</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>成交價</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>委託時間</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>損益</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 500 }}>狀態</th>
                  <th style={{ padding: '10px 24px 10px 16px', textAlign: 'left', fontWeight: 500 }}>交易緣由</th>
                </tr>
              </thead>
              <tbody>
                {POSITIONS.map((p, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${VA.ruleSoft}` }}>
                    <td style={{ padding: '14px 16px 14px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%',
                          background: ['#D6CEB9','#C3D6CC','#E4D5C6','#D4C8DB','#DBCEC8','#C8D0DB'][TRADERS.findIndex(t=>t.name===p.trader)] || '#D6CEB9',
                          display: 'grid', placeItems: 'center',
                          fontSize: FontSizes.trading_xs, fontWeight: 600,
                        }}>{p.trader.slice(0,1).toUpperCase()}</div>
                        <span style={{ fontWeight: 500 }}>{p.trader}</span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontWeight: 500 }}>{p.sym}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 9px', borderRadius: 3,
                        fontSize: FontSizes.trading_lg, fontWeight: 600, letterSpacing: '0.05em',
                        background: p.side === 'B' ? VA.accentSoft : '#F8ECEB',
                        color: p.side === 'B' ? VA.accent : VA.down,
                      }}>{p.side === 'B' ? '多' : '空'}</span>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontFamily: VA.mono, fontVariantNumeric: 'tabular-nums' }}>{p.lots}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', fontFamily: VA.mono, fontVariantNumeric: 'tabular-nums' }}>{p.price}</td>
                    <td style={{ padding: '14px 16px', color: VA.inkSoft, fontFamily: VA.mono, fontSize: FontSizes.xxs }}>{p.ot}</td>
                    <td style={{
                      padding: '14px 16px', textAlign: 'right',
                      fontFamily: VA.mono, fontVariantNumeric: 'tabular-nums',
                      fontWeight: 500,
                      color: p.pnl >= 0 ? VA.up : VA.down,
                    }}>{fmt.pnl(p.pnl)}</td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: FontSizes.trading_lg, color: VA.inkSoft,
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: p.st === 'holding' ? VA.warn : VA.inkFaint,
                        }}/>
                        {p.st === 'holding' ? '留倉' : '已平'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 24px 14px 16px', color: VA.inkSoft, fontStyle: 'italic', fontFamily: VA.serif, fontSize: FontSizes.trading_md }}>
                      "{p.note}"
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Colophon */}
          <div style={{
            marginTop: 28, paddingTop: 16,
            borderTop: `1px solid ${VA.rule}`,
            fontSize: FontSizes.trading_lg, color: VA.inkFaint,
            display: 'flex', justifyContent: 'space-between',
            fontFamily: VA.mono, letterSpacing: '0.04em',
          }}>
            <span>GENESIS INTERNATIONAL CAPITAL CORP. · TRADING DESK EDITION</span>
            <span>Last sync 14:22:08 · Delayed 15m</span>
          </div>
        </div>
      </main>
    </div>
  );
}

/* ─────── Sub components for Variant A ─────── */
function EditorialChart({ accountTab, daily } = {}) {
  // 資料來源：/positions/equity-daily（每日「權益相對原始資金」報酬率 %，後端算好）。
  // data 已是 %，直接畫。只認 daily prop，避免誤用舊的 NT$ 序列（單位不符）。
  const src = (daily && Array.isArray(daily.series)) ? daily.series : [];
  const dates = (daily && Array.isArray(daily.days)) ? daily.days : [];
  const series = accountTab
    ? src.filter(s => (accountTab === 'trading' ? s.name === '5571391' : s.name === '5571155'))
    : src;

  // 無資料 → 空狀態（不再 fallback 成設計稿假人名，避免誤導）
  if (!series.length || !series.some(s => (s.data || []).length)) {
    return (
      <div style={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: VA.inkFaint, fontSize: 13 }}>
        尚無權益資料
      </div>
    );
  }

  const fmt = (v) => (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
  // 領跑 = 最終累計最大者
  const leader = series.reduce((best, s) => {
    const last = s.data[s.data.length - 1] || 0;
    const bestLast = best ? (best.data[best.data.length - 1] || 0) : -Infinity;
    return last > bestLast ? s : best;
  }, null);
  const leaderName = leader ? leader.name : null;
  const all = series.flatMap(s => s.data);
  // 對稱 Y 軸 → 0% 線固定置中（上下各 ±M）
  const M = Math.max(Math.abs(Math.min(...all)), Math.abs(Math.max(...all)), 0.01);
  const min = -M, max = M;
  const range = max - min;                 // = 2M，恆 > 0（不會 NaN）
  const w = 700, h = 220, padR = 64;       // 右側留白給名稱 + 數值標籤
  const plotW = w - padR;
  const x = i => (i / Math.max(1, series[0].data.length - 1)) * plotW;
  const y = v => h - ((v - min) / range) * h;

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h + 22}`} style={{ width: '100%', height: 240 }}>
        {/* grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
          <line key={i} x1={0} x2={plotW} y1={p*h} y2={p*h} stroke={VA.ruleSoft} strokeWidth="1" strokeDasharray={i===0 || i===4 ? '' : '2 3'} />
        ))}
        {/* Y 軸 max / min 刻度 */}
        <text x={2} y={10} fontSize="9" fill={VA.inkFaint} fontFamily={VA.mono}>{fmt(max)}</text>
        <text x={2} y={h - 3} fontSize="9" fill={VA.inkFaint} fontFamily={VA.mono}>{fmt(min)}</text>
        {/* zero line（對稱軸 → 固定置中）*/}
        <line x1={0} x2={plotW} y1={y(0)} y2={y(0)} stroke={VA.inkFaint} strokeWidth="1" />
        <text x={2} y={y(0) - 3} fontSize="9" fill={VA.inkFaint} fontFamily={VA.mono}>0%</text>
        {/* X 軸起訖日期 */}
        {dates.length > 0 && (
          <g>
            <text x={0} y={h + 17} fontSize="9" fill={VA.inkFaint} fontFamily={VA.mono}>{dates[0]}</text>
            <text x={plotW} y={h + 17} fontSize="9" fill={VA.inkFaint} fontFamily={VA.mono} textAnchor="end">{dates[dates.length-1]}</text>
          </g>
        )}
        {/* series */}
        {series.map((s, si) => {
          const d = s.data.map((v, i) => (i === 0 ? `M${x(i)},${y(v)}` : `L${x(i)},${y(v)}`)).join(' ');
          const isLeader = s.name === leaderName;
          const lastV = s.data[s.data.length - 1] || 0;
          return (
            <g key={si}>
              {isLeader && <path d={d + ` L${x(s.data.length-1)},${h} L0,${h} Z`} fill={s.color} opacity="0.08" />}
              <path d={d} fill="none" stroke={s.color} strokeWidth={isLeader ? 2 : 1.3} opacity={isLeader ? 1 : 0.75} />
              {/* end marker + 名稱 + 累計報酬率 % */}
              <circle cx={x(s.data.length-1)} cy={y(lastV)} r={isLeader ? 3 : 2} fill={s.color} />
              <text x={x(s.data.length-1) + 6} y={y(lastV) - 2}
                fontSize="10" fill={s.color} fontFamily={VA.mono} fontWeight={isLeader ? 600 : 500}>{s.name}</text>
              <text x={x(s.data.length-1) + 6} y={y(lastV) + 9}
                fontSize="9" fill={s.color} fontFamily={VA.mono}>{fmt(lastV)}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function SubCard({ title, fig, children }) {
  return (
    <div style={{ background: VA.surface, border: `1px solid ${VA.rule}`, borderRadius: 6, padding: '18px 20px' }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: FontSizes.trading_md, letterSpacing: '0.14em', textTransform: 'uppercase', color: VA.inkFaint, marginBottom: 3 }}>
          Figure {fig}
        </div>
        <h4 style={{ fontFamily: VA.serif, fontSize: FontSizes.md, fontWeight: 400, margin: 0, letterSpacing: '-0.01em' }}>
          {title}
        </h4>
      </div>
      {children}
    </div>
  );
}

function DistBars() {
  const data = [
    ['2330 台積電', 28],
    ['小台指',      22],
    ['2454 聯發科', 14],
    ['2317 鴻海',   12],
    ['2408 南亞科', 10],
    ['其他',        14],
  ];
  const max = Math.max(...data.map(d => d[1]));
  return (
    <div style={{ paddingTop: 4 }}>
      {data.map(([l, v], i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '92px 1fr 32px', gap: 10, alignItems: 'center', marginBottom: 9 }}>
          <span style={{ fontSize: FontSizes.trading_xl, color: VA.inkSoft }}>{l}</span>
          <div style={{ height: 10, background: VA.ruleSoft, borderRadius: 1, overflow: 'hidden' }}>
            <div style={{ width: `${v/max*100}%`, height: '100%', background: i === 0 ? VA.accent : `oklch(55% 0.05 ${170 - i*12})` }} />
          </div>
          <span style={{ fontSize: FontSizes.trading_lg, fontFamily: VA.mono, color: VA.inkSoft, textAlign: 'right' }}>{v}%</span>
        </div>
      ))}
    </div>
  );
}

function DailyBars() {
  const days = ['10/16','10/17','10/18','10/19','10/22'];
  const vals = [38, 52, 44, 71, 48];
  const max = Math.max(...vals);
  return (
    <div style={{ paddingTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 120, gap: 10 }}>
        {vals.map((v, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: FontSizes.trading_md, color: VA.inkFaint, fontFamily: VA.mono }}>{v}</span>
            <div style={{
              width: '100%', height: `${v/max*100}%`,
              background: i === 3 ? VA.accent : '#D9D4C6',
              borderRadius: '1px 1px 0 0',
            }}/>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${VA.ruleSoft}`, marginTop: 4 }}>
        {days.map((d, i) => (
          <span key={i} style={{ fontSize: FontSizes.trading_md, color: VA.inkFaint, fontFamily: VA.mono, flex: 1, textAlign: 'center' }}>{d}</span>
        ))}
      </div>
    </div>
  );
}

function SessionHeat() {
  const hours = ['09','10','11','12','13','14'];
  const r = seededRandom(42);
  const grid = Array.from({ length: 5 }, () => Array.from({ length: 6 }, () => r()));
  return (
    <div style={{ paddingTop: 8 }}>
      {grid.map((row, ri) => (
        <div key={ri} style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
          {row.map((v, ci) => (
            <div key={ci} style={{
              flex: 1, aspectRatio: '1.4 / 1',
              background: `oklch(${75 - v*35}% ${v*0.08} 195)`,
              borderRadius: 2,
            }}/>
          ))}
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, marginTop: 4, borderTop: `1px solid ${VA.ruleSoft}` }}>
        {hours.map((h, i) => (
          <span key={i} style={{ fontSize: FontSizes.trading_sm, color: VA.inkFaint, fontFamily: VA.mono, flex: 1, textAlign: 'center' }}>{h}</span>
        ))}
      </div>
    </div>
  );
}

window.VariantA = VariantA;
Object.assign(window, {
  EditorialChart: typeof EditorialChart !== 'undefined' ? EditorialChart : null,
  SubCard: typeof SubCard !== 'undefined' ? SubCard : null,
  DistBars: typeof DistBars !== 'undefined' ? DistBars : null,
  DailyBars: typeof DailyBars !== 'undefined' ? DailyBars : null,
});
